/**
 * @file    aliasResolver.ts
 * @scope   SHARED
 * @summary Pure helpers that answer "which widget/alias/rule does this chart selection map to?".
 *          Extracted from DiyDrawer so the resolution rules are testable without React.
 * @consumes DIYChartOption, Widget, WidgetAlias models; `library` alias catalogue
 * @produces resolveWidgetByChartType, getOptionsByAlias, hasAliasChanged, resolveWidgetAlias
 * @sideEffects None. Every function is pure; callers own the state updates.
 */
import { library } from 'components';
import { DIYChartOption } from 'constants/chart-selector';
import { ChartMapping, Widget, WidgetAlias } from 'models/WidgetModel';

/** Minimal widget descriptor produced when a chart type is matched against the widget catalogue. */
export type ResolvedChartWidget = {
  /** Component name registered in the widget library (e.g. `DynamicTable`). */
  alias: string;
  /** Guardrails controller backing the chart type. */
  controllerName: string;
  /** Business rules the chart type accepts. */
  rules?: string[];
};

/**
 * Finds the first widget in the catalogue that can render `type` for the current data source.
 *
 * A widget matches when either one of its aliases exposes the requested controller, or its own
 * component name equals the chart type — and it supports the dashboard's data source type.
 *
 * @param type          Chart option returned by Guardrails.
 * @param widgetsList   Widget catalogue from the global store.
 * @param dataSourceType Data source types of the dashboard (only the first entry is checked).
 * @returns The resolved widget, or `undefined` when no widget can render the chart type.
 */
export const resolveWidgetByChartType = (
  type: DIYChartOption,
  widgetsList: any[],
  dataSourceType: string[]
): ResolvedChartWidget | undefined => {
  for (const widget of widgetsList) {
    if (!widget.alias?.length) continue;

    const matchedAlias = widget.alias.find(
      (alias: WidgetAlias) => alias.controllerName === type.chartType
    );
    const matchesComponent = widget.component === type.chartType;
    const supportsDataSource = widget.dataSourceTypes.includes(dataSourceType[0]);

    if ((matchedAlias || matchesComponent) && supportsDataSource) {
      return {
        alias: widget.component,
        controllerName: matchedAlias?.controllerName || widget.component,
        rules: type.rules,
      };
    }
  }

  return undefined;
};

/**
 * Resolves the key used to look up entries in `diyDrawerOptions` / `diyActionsOptions`.
 *
 * WHY: child charts (DataGrid variants) share a single alias, so their options are keyed by the
 * business rule name instead. Non-child charts keep using their alias.
 *
 * @param alias                 Current widget alias.
 * @param selectedDiyChildChart Chart option currently selected in the dropdown (may be a child).
 */
export const getOptionsByAlias = (alias: string, selectedDiyChildChart?: any): string => {
  if (!selectedDiyChildChart?.isChild) return alias;

  const [ruleName] = selectedDiyChildChart.rules || [];
  return ruleName ? ruleName : alias;
};

/**
 * Decides whether the newly selected chart type differs from what the widget currently renders,
 * which is the trigger to rebuild the widget from scratch.
 *
 * WHY: for DataGrid the alias alone is not enough — two selections can share the `DynamicTable`
 * alias while pointing at different business rules, so the rule is compared too.
 *
 * @param chartType        Chart type of the newly selected option.
 * @param alias            Alias of the widget currently being edited.
 * @param configRules      Rules stored in the widget config (`customWidgetConfig.Rules`).
 * @param currentAliasRules Rules of the currently selected chart option.
 */
export const hasAliasChanged = (
  chartType: string,
  alias: string,
  configRules: any[] = [],
  currentAliasRules: string[] = []
): boolean => {
  if (alias === library.DYNAMIC_TABLE || alias === library.DYNAMIC_TABLE_OLD) {
    const [selectedRule] = configRules;
    const [selectedCurrentRule] = currentAliasRules;
    const ruleChanged = selectedRule === undefined || selectedRule.ruleName !== selectedCurrentRule;
    return chartType !== 'DataGrid' || ruleChanged;
  }

  return chartType !== alias;
};

/**
 * Resolves the alias persisted in `customWidgetConfig.Chart`, walking the possible sources in
 * priority order: dropdown selection → freshly created widget → saved payload → saved config.
 *
 * @param currentAlias Chart option selected in the dropdown (if any).
 * @param customWidget Widget currently being edited.
 * @returns The chart alias, or `'Unknown'` when none could be resolved.
 */
export const resolveWidgetAlias = (
  currentAlias: DIYChartOption | undefined,
  customWidget: Widget | any
): string => {
  // A chart was picked from the dropdown and carries its own alias.
  if (currentAlias?.chartType && currentAlias?.chartType !== '' && currentAlias?.alias)
    return currentAlias.alias;

  // Widget was just created and is not saved yet.
  if (customWidget.controllerName) return customWidget.controllerName;

  // Widget is already saved and the controller name travels in the payload.
  if (customWidget?.payload?.ControllerName) return customWidget.payload.ControllerName;

  const chart = customWidget.customWidgetConfig?.chart;
  if (chart) {
    // WHY: legacy configs stored `Chart` as a numeric enum, newer ones store the alias string.
    return typeof chart !== 'string'
      ? ChartMapping[chart as keyof typeof ChartMapping] || 'Unknown'
      : chart;
  }

  return 'Unknown';
};
